import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../../i18n';
import { RootState } from '../../store';

const { t } = i18n;

interface IReferralsState {
  error: string;
  success: boolean;
  recaptchaPassed: boolean;
  showLoader: boolean;
  receivedCustomerType: string;
}

type Payload = {
  isRenter: boolean | null;
  userEmail: string;
  orderNumber: string;
  referredEmail: string;
};

export const sendReferralRequest = createAsyncThunk(
  'referrals/sendReferralRequest',
  async (body: Payload, { dispatch }) => {
    const err: string = validatePayload(body);
    if (err) {
      dispatch(gotError(err));
      return null;
    }

    const response = await fetch(
      `${process.env.REACT_APP_ORDER_SERVICE_API}/referrals/refer`,
      {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }
    );
    if (response.status >= 300) {
      switch (response.status) {
        case 404:
          throw new Error(
            t(
              'order_not_found',
              'We’re sorry, but we can’t find that order number. Please double check your order and email address and try again.'
            )
          );
        case 403:
          throw new Error(
            t(
              'order_not_eligible',
              'We’re sorry, but this order number is not eligible for making referrals at this time. Connect with us at customercare@happiestbaby.com if you have any questions.'
            )
          );
        case 409:
          throw new Error(
            t(
              'user_already_invited',
              'User with provided email is already invited.'
            )
          );
        default:
          throw new Error(
            t(
              'generic_error',
              'Oops…something went wrong! Please check your email address and order number and try again. If you need help, please connect with us at customercare@happiestbaby.com.'
            )
          );
      }
    }
    return response.json();
  }
);

const validatePayload = (payload: Payload): string => {
  if (payload.isRenter === null) {
    return t('choose_snoo_option', 'Please choose a SNOO option.');
  } else if (!payload.userEmail) {
    return t('email_payload_validation', 'Please enter email.');
  } else if (!payload.orderNumber) {
    return t('order_number_payload_validation', 'Please enter order number.');
  } else if (!payload.referredEmail) {
    return t(
      'recipient_email_payload_validation',
      'Please enter recipient email.'
    );
  }
  return '';
};
const initialState: IReferralsState = {
  error: '',
  success: false,
  recaptchaPassed: false,
  showLoader: false,
  receivedCustomerType: '',
};

export const referralsSlice = createSlice({
  name: 'referrals',
  initialState,
  reducers: {
    recaptchaVerified: (
      state: IReferralsState,
      action: PayloadAction<string | null>
    ) => {
      state.recaptchaPassed = !!action.payload;
    },
    gotError: (state: IReferralsState, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.success = false;
      state.showLoader = false;
      state.receivedCustomerType = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendReferralRequest.pending, (state: IReferralsState) => {
      state.showLoader = true;
    });
    builder.addCase(
      sendReferralRequest.fulfilled,
      (state: IReferralsState, action: PayloadAction<any>) => {
        const response = action.payload;
        state.error = '';
        state.success = true;
        state.showLoader = false;
        state.receivedCustomerType = response.customerType || '';
      }
    );
    builder.addCase(
      sendReferralRequest.rejected,
      (state: IReferralsState, action) => {
        if (action.error.message) {
          state.error = action.error.message;
        } else {
          state.error = '';
        }
        state.success = false;
        state.showLoader = false;
        state.receivedCustomerType = '';
      }
    );
  },
});

export const { recaptchaVerified, gotError } = referralsSlice.actions;

export const selectReferrals = (state: RootState) => {
  return {
    ...state.referrals,
  };
};

export default referralsSlice.reducer;
